'use client'

import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { publicEnv } from '@/config/public-env'

type TurnstileWidgetId = string

type Turnstile = {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string
      callback: (token: string) => void
      'expired-callback': () => void
      'error-callback': () => void
      language?: string
      theme?: 'auto' | 'light' | 'dark'
    }
  ) => TurnstileWidgetId
  remove: (widgetId: TurnstileWidgetId) => void
  reset: (widgetId: TurnstileWidgetId) => void
}

declare global {
  interface Window {
    turnstile?: Turnstile
  }
}

type TurnstileCaptchaProps = {
  resetKey: number
  onError: () => void
  onExpire: () => void
  onVerify: (token: string) => void
}

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script'
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

const loadTurnstileScript = () => {
  if (typeof window === 'undefined') return

  if (document.getElementById(TURNSTILE_SCRIPT_ID)) return

  const script = document.createElement('script')
  script.id = TURNSTILE_SCRIPT_ID
  script.src = TURNSTILE_SCRIPT_SRC
  script.async = true
  script.defer = true
  document.head.appendChild(script)
}

const TurnstileCaptcha = ({ resetKey, onError, onExpire, onVerify }: TurnstileCaptchaProps) => {
  const { i18n, t } = useTranslation()
  const id = useId()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<TurnstileWidgetId | null>(null)
  const siteKey = publicEnv.NEXT_PUBLIC_HAFIZ_TURNSTILE_SITE_KEY
  const [isUnavailable, setIsUnavailable] = useState(false)

  useEffect(() => {
    if (!siteKey) {
      return
    }

    loadTurnstileScript()

    const renderWidget = () => {
      if (!window.turnstile || !containerRef.current || widgetIdRef.current) return false

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme: 'light',
        language: i18n.language?.split('-')[0],
        callback: onVerify,
        'expired-callback': onExpire,
        'error-callback': () => {
          setIsUnavailable(true)
          onError()
        },
      })

      return true
    }

    if (renderWidget()) return

    const interval = window.setInterval(() => {
      if (renderWidget()) {
        window.clearInterval(interval)
      }
    }, 100)

    return () => {
      window.clearInterval(interval)

      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [i18n.language, onError, onExpire, onVerify, siteKey])

  useEffect(() => {
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current)
    }
  }, [resetKey])

  return (
    <div className='flex min-h-[74px] flex-col items-center gap-2'>
      <div ref={containerRef} id={id} />
      {isUnavailable ? <p className='text-center text-lg text-red-100'>{t('register.captcha.unavailable')}</p> : null}
    </div>
  )
}

export default TurnstileCaptcha
